import React, { useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import {
  type GetThemeValueForKey,
  Sheet as TamaguiSheet,
  type SheetProps as TamaguiSheetProps,
  useWindowDimensions,
  View,
} from "tamagui";
import { trackEvent } from "../core/trackEvent";

export type SheetProps = TamaguiSheetProps & {
  sheetId: string;

  unclosable?: boolean;
  scrollable?: boolean;
  maxHeightPercentage?: number;
  alwaysRender?: boolean;
  hideOverlay?: boolean;
  noPadding?: boolean;
  withShadow?: boolean;
  isLoading?: boolean;

  header?: JSX.Element;
  footer?: JSX.Element;
  footerOptions?: {
    noPaddingTop?: boolean;
  };
};

export const Sheet: React.FC<SheetProps> = ({
  sheetId,
  children,
  unclosable,
  isLoading,
  scrollable = true,
  alwaysRender,
  maxHeightPercentage,
  hideOverlay,
  noPadding,
  withShadow,
  header,
  footer,
  footerOptions,
  ...props
}) => {
  const windowHeight = useWindowDimensions().height;
  const [render, setRender] = useState(props.open || alwaysRender || false);
  const maxHeight =
    props.snapPointsMode === "constant"
      ? undefined
      : windowHeight * (maxHeightPercentage ?? 0.85);

  let content: JSX.Element | undefined;

  React.useEffect(() => {
    if (render) {
      trackEvent(`sheet_opened_${sheetId}`);

      return () => {
        trackEvent(`sheet_closed_${sheetId}`);
      };
    }
  }, [render, sheetId]);

  const padding = noPadding ? undefined : "$space.larger";
  if (render) {
    const hasContent = !!children;

    content = (
      <View maxHeight={maxHeight} flex={1}>
        <TamaguiSheet.ScrollView flex={1} scrollEnabled={scrollable}>
          {header && (
            <View
              paddingHorizontal={padding}
              backgroundColor={"$backgroundBlock"}
            >
              <View
                paddingTop={padding}
                paddingBottom="$space.medium"
                borderBottomWidth={1}
                borderBottomColor={"$divider"}
              >
                {header}
              </View>
            </View>
          )}
          {hasContent && (
            <View
              flex={1}
              paddingHorizontal={padding}
              paddingTop={header ? "$space.medium" : padding}
              paddingBottom={footer ? "$space.medium" : padding}
            >
              {children}
            </View>
          )}

          {footer && (
            <View paddingHorizontal={padding}>
              <View
                paddingBottom={padding}
                paddingTop={
                  footerOptions?.noPaddingTop
                    ? 0
                    : footer
                      ? "$space.medium"
                      : padding
                }
                borderTopWidth={hasContent ? 1 : 0}
                borderTopColor={"$divider"}
              >
                {footer}
              </View>
            </View>
          )}
        </TamaguiSheet.ScrollView>

        {isLoading && (
          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ActivityIndicator size={"large"} color={"#ffd84e"} />
          </View>
        )}
      </View>
    );
  }

  useEffect(() => {
    if (props.open === render) {
      return;
    }

    if (props.open) {
      setRender(true);
    } else {
      const timeout = setTimeout(() => {
        setRender(false);
      }, 500);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [props.open, render]);

  const shadowProps = withShadow
    ? {
        shadowColor: "#000" as GetThemeValueForKey<"color">,
        shadowOpacity: 0.15,
        shadowRadius: 24,
        shadowOffset: { height: -8, width: 0 },
      }
    : undefined;

  if (!render) {
    return null;
  }

  return (
    <TamaguiSheet
      modal
      snapPointsMode="mixed"
      snapPoints={["fit"]}
      animation="quick"
      forceRemoveScrollEnabled={props.open}
      dismissOnOverlayPress={!unclosable}
      dismissOnSnapToBottom={!unclosable}
      moveOnKeyboardChange={false}
      disableDrag={true}
      animationConfig={{
        type: "spring",
        overshootClamping: true,
      }}
      {...props}
    >
      {!hideOverlay && (
        <TamaguiSheet.Overlay
          backgroundColor={"rgba(0,0,0,0.8)"}
          animation="medium"
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
      )}
      <TamaguiSheet.Frame
        {...shadowProps}
        maxHeight={maxHeight}
        backgroundColor={"$backgroundBlock"}
        borderRadius={0}
      >
        <View alignSelf="center" maxWidth={600} width="100%">
          {content}
        </View>
      </TamaguiSheet.Frame>
    </TamaguiSheet>
  );
};
